import React, { useState, useEffect } from 'react';
import { getMode, setAutomation, seedLabCoverage, seedVendorCoverage } from '../lib/fax-api';

// The independent automation capabilities, in display order. Each replaces
// what used to be a single coarse "mode" pill (Paused/Shadow/Misc/Full).
const TOGGLES = [
  { key: 'autoFileMisc',       label: 'Auto-file misc documents',  desc: 'Auto-file misc documents to the chart' },
  { key: 'autoFileStructured', label: 'Auto-file labs / imaging',  desc: 'Auto-file labs / imaging (structured reports)' },
  { key: 'draftRefills',       label: 'Draft refill eRx',          desc: 'Draft refill eRx from refill-request faxes' },
  { key: 'resolveRefills',     label: 'Resolve refills (RxNorm)',  desc: 'Resolve refills via RxNorm + surface un-resolvable ones for review' },
  { key: 'signForms',          label: 'Auto-sign forms',           desc: 'Auto-place provider e-signature on signature forms (allowlist-gated)' },
  { key: 'formFill',           label: 'Form-fill assistant',       desc: 'Send fillable forms (e.g. ICD-10 deficiency notices) to the form-fill review screen' },
  { key: 'coverageGrounding',  label: 'Coverage grounding',        desc: 'Annotate form-fill dx suggestions with Medicare coverage (covered / ABN / denied) from the seeded lab-coverage table' },
  { key: 'postFillQa',         label: 'Form-fill render check',    desc: 'Re-read every rendered form, auto-correct tick marks that missed their box, and warn about anything else suspicious — never blocks a send' },
];

// Read a (gzipped) File as base64 — robust for binary: readAsDataURL gives a
// `data:...;base64,<payload>` string, so we just strip the prefix. Avoids the
// chunked String.fromCharCode + btoa dance and never overflows the call stack
// on a multi-MB buffer.
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('file read failed'));
    reader.readAsDataURL(file);
  });
}

// Generic admin gzipped-CSV uploader: reads the small .gz, base64s it, POSTs via
// `upload`, renders the per-seed result via `renderResult`. The server gunzips +
// parses + ingests. Used for both the CMS lab-coverage table and the Northwell
// vendor decision-support table.
function SeedUpload({ label, desc, upload, renderResult }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  async function onFile(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setBusy(true); setError(null); setResult(null);
    try {
      setResult(await upload(await fileToBase64(file)));
    } catch (err) {
      setError(err.message || 'seed failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings-row fax-automation-row">
      <div style={{ flex: 1 }}>
        <div className="settings-row-label">{label}</div>
        <div className="settings-row-desc">{desc}</div>
        {result && <div className="settings-row-desc">{renderResult(result)}</div>}
        {error && <div className="fax-mode-error">Error: {error}</div>}
      </div>
      <label className="btn btn-secondary" style={{ cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
        {busy ? 'Seeding…' : 'Upload .gz'}
        <input type="file" accept=".gz,.csv.gz,application/gzip" disabled={busy} onChange={onFile} style={{ display: 'none' }} />
      </label>
    </div>
  );
}

export function FaxModeSwitch({ onChange }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false); // AUTOMATION section collapse

  useEffect(() => { getMode().then(setState).catch(e => setError(e.message)); }, []);

  async function toggle(key, value) {
    if (!state || !state.can_edit || busy) return;
    setBusy(true); setError(null);
    try {
      const r = await setAutomation({ [key]: value });
      // Server returns the full, authoritative automation map (untouched flags
      // are seeded). Adopt it rather than optimistically guessing.
      setState(prev => prev ? { ...prev, automation: r.automation } : prev);
      if (onChange) onChange(r.automation);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (state === null && error) return <div className="fax-mode-error">{error}</div>;
  if (state === null) return <div className="fax-mode-loading">Loading automation…</div>;

  const automation = state.automation || {};
  const readOnly = !state.can_edit;
  const onCount = TOGGLES.filter(t => automation[t.key] === true).length;

  return (
    <div className="fax-automation-toggles" role="group" aria-label="Fax automation capabilities">
      {/* Collapsible section header — the panel has grown long (toggles + seeds). */}
      <button
        type="button"
        className="fax-automation-title"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed(c => !c)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 0, padding: 0, margin: 0, cursor: 'pointer' }}
      >
        <span aria-hidden="true" style={{ fontSize: 11, lineHeight: 1 }}>{collapsed ? '▸' : '▾'}</span>
        Automation
        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--ink-3)' }}>
          {onCount} of {TOGGLES.length} on
        </span>
      </button>

      {!collapsed && (
        <>
          {TOGGLES.map(({ key, label, desc }) => (
            <div className="settings-row fax-automation-row" key={key}>
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">{label}</div>
                <div className="settings-row-desc">{desc}</div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={automation[key] === true}
                  disabled={busy || readOnly}
                  onChange={(e) => toggle(key, e.target.checked)}
                  aria-label={desc}
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>
          ))}
          {readOnly && <div className="fax-automation-readonly-note">Read-only — ask an admin to change automation.</div>}
          {/* Admin-only (can_edit ⇒ admin per GET /api/fax-inbox/mode). */}
          {!readOnly && (
            <>
              <SeedUpload
                label="Seed lab coverage"
                desc={<>Load the lab-coverage reference table from a gzipped CSV (run <code>gzip lab-coverage.csv</code> first; upload the <code>.gz</code>).</>}
                upload={seedLabCoverage}
                renderResult={(r) => `Loaded ${r.inserted} rows (${r.ncds} NCDs, ${r.cpts} CPTs) from ${r.sourceFile}.`}
              />
              <SeedUpload
                label="Seed Northwell coverage"
                desc={<>Load the Northwell decision-support ICD-10 list (the <code>import-northwell-coverage.js --out-csv</code> export, gzipped).</>}
                upload={seedVendorCoverage}
                renderResult={(r) => `Loaded ${r.inserted} rows across ${r.sections} sections (${r.vendorKey}).`}
              />
            </>
          )}
          {error && <div className="fax-mode-error">Error: {error}</div>}
        </>
      )}
    </div>
  );
}
